<template>
  <div v-if="slideGroups.length > 0" class="the-banner" :class="`the-banner--${bannerOrientation}`">
    <ClientOnly>
      <!-- Показываем только если слайды есть и их больше 0 -->
      <section class="banner">
        <swiper-container
          ref="containerRef"
          :key="bannerOrientation"
          space-between="30"
          сentered-slides="true"
          autoplay-delay="8000"
          autoplay-disable-on-interaction="false"
          :scrollbar="{}"
          :breakpoints="bannerBreakpoints"
        >
          <swiper-slide
            v-for="(group, idx) in slideGroups"
            :key="group.group_id || group.cover.id"
            class="cursor-pointer slide__image"
            @click="openStory(idx)"
          >
            <!-- Видео-обложка (когда нет изображения, но есть видео) -->
            <video
              v-if="!group.cover.image_url && group.cover.video_url"
              :src="group.cover.video_url"
              muted
              loop
              autoplay
              playsinline
              class="w-full h-full object-cover pointer-events-none"
            />
            <!-- Обычная обложка -->
            <img v-else-if="group.cover.image_url" :src="group.cover.image_url" :alt="group.cover.title || 'Story'" />
            <!-- Заглушка (нет ни изображения, ни видео — скрыть слайд не выйдет в v-for, но визуально пусто) -->
            <div v-else class="w-full h-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center rounded-xl">
              <UIcon name="i-heroicons-photo" class="w-8 h-8 text-gray-400" />
            </div>

            <!-- Индикатор видео-слайда -->
            <div v-if="!group.cover.image_url && group.cover.video_url" class="slide__video-badge">
              <UIcon name="i-heroicons-play-circle" class="w-4 h-4" />
            </div>
          </swiper-slide>
        </swiper-container>
      </section>
    </ClientOnly>

    <StoryViewer
      v-if="showViewer"
      :slide-groups="slideGroups"
      :initial-group-index="startIndex"
      @close="showViewer = false"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import apiNewsSlider, { groupSlides } from '~/entities/news-slider/api';
import type { NewsSliderV2, SlideGroup } from '~/entities/news-slider/api';
import { getReadyInstitutionId, useAppearance } from '~/entities/institution/@x/news-slider';
import { BANNER_BREAKPOINTS } from '@shared/config';

// Ориентация плиток — настройка заведения: вертикальные «сторис» или широкие баннеры 16:9.
// Разное число плиток в ряду — оттуда же, иначе широкий баннер сжимался бы до полоски.
const { appearance } = useAppearance();
const bannerOrientation = computed(() => appearance.value.banners.orientation);
const bannerBreakpoints = computed(() => BANNER_BREAKPOINTS[bannerOrientation.value]);

const slides = ref<NewsSliderV2[]>([]);
const containerRef = ref<any>(null);

const showViewer = ref(false);
const startIndex = ref(0);

// Группируем слайды — показываем только те, у которых есть хотя бы одно медиа
const slideGroups = computed<SlideGroup[]>(() =>
  groupSlides(slides.value).filter((g) => g.cover.image_url || g.cover.video_url),
);

// Загружаем слайды с API
const fetchSlides = async () => {
  try {
    const institutionID = await getReadyInstitutionId();
    slides.value = await apiNewsSlider.getPublic(institutionID);
  } catch {
    slides.value = [];
  }
};

onMounted(() => {
  fetchSlides();
});

function openStory(idx: number) {
  const group = slideGroups.value[idx];
  if (!group || group.slides.length === 0) {
    return;
  }
  // Блокировку скролла фона берёт на себя сам StoryViewer (lock на mount).
  startIndex.value = idx;
  showViewer.value = true;
}
</script>

<style lang="css" scoped>
.banner {
  @apply py-5;
}
/*
  Пропорцию плитки задаёт кадр, а не экран.

  Раньше вертикальным плиткам задавалась ВЫСОТА (30dvh с зажимом 246–300px), а ширину
  давал слайдер из slidesPerView. Пропорция получалась какая придётся: на широком экране
  плитка выходила почти квадратной, и вертикальный кадр в ней обрезался сверху и снизу —
  ровно то, из-за чего сторисы выглядели сплющенными. Теперь у обеих ориентаций работает
  один приём: `height: auto` + `aspect-ratio`, как это с самого начала было у горизонтальных.
*/
swiper-slide {
  display: flex;
  justify-content: center;
  align-items: center;
  height: auto;
  /* Вертикальные плитки — формат рилса: 9:16 на любом экране и при любом числе в ряду */
  aspect-ratio: 9 / 16;
  font-size: 4rem;
  font-weight: bold;
  font-family: 'Roboto', sans-serif;
}

/* Горизонтальные баннеры: широкий кадр 16:9 — на высокой плитке он обрезался бы по бокам. */
.the-banner--horizontal swiper-slide {
  aspect-ratio: 16 / 9;
}

.slide__image {
  @apply overflow-hidden mb-6 relative;
  border-radius: var(--app-radius-media, 16px);

  img {
    @apply w-full h-full object-cover;
  }
}

.slide__count {
  @apply absolute top-2 right-2 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded-full;
}

.slide__video-badge {
  @apply absolute bottom-2 left-2 bg-black bg-opacity-60 text-white rounded-full p-1 h-fit flex;
}
</style>
